// ─────────────────────────────────────────────────────────────────────────────
// Harnais de non-régression Appee — branché sur la VRAIE logique de l'app.
//
// But : verrouiller les bugs déjà corrigés (dates 2 chiffres, 473 doublons,
// consentement nLPD…) pour qu'ils ne reviennent JAMAIS, et auditer les briques
// de calcul critiques (SMS facturés au segment, marge par client, insights).
//
// Chaque test importe la fonction réelle — si le code change et casse un
// comportement attendu, le test passe au rouge. Zéro mock, zéro doublon de logique.
// ─────────────────────────────────────────────────────────────────────────────
import { normalizeBirthDate, contactAge, localDateStr } from '../utils/dateUtils';
import { smsInfo, smsSegments, SMS_OPTOUT_LEN, smsFromEmailBody } from '../utils/sms';
import { calcAiScore } from '../utils/companyScore';
import { autoDateStr, autoCampaignName } from '../utils/campaignName';
import { SWISS_CANTONS, isSwissCanton, swissCantons } from '../utils/cantons';
import { contactMatchesQuery } from '../utils/contactSearch';
import { QUALITY_FIELDS, qualityScore, qualityBreakdown } from '../utils/contactQuality';
import { getAvatarColor, getAvatarStyle } from '../utils/avatar';
import {
  normalizePhone, findDuplicates, duplicateContactCount,
  unreachable, invalidEmails, invalidPhones, noConsentList,
  vipDormant, birthdaySoonUnreachable, quotaPressure, computeInsights, isSwissMobile,
} from '../utils/dataInsights';
import { isVip } from '../utils/segments';
import { PROFILE_ESSENTIALS, missingEssentials, essentialLink } from '../utils/profileEssentials';

// ── Mini-framework d'assertions (aucune dépendance) ─────────────────────────
function makeSuite() {
  const cats = [];
  function cat(key, label) {
    const c = { key, label, cases: [] };
    cats.push(c);
    const eq = (name, got, want) =>
      c.cases.push({ name, pass: JSON.stringify(got) === JSON.stringify(want), got, want });
    const ok = (name, cond) =>
      c.cases.push({ name, pass: !!cond, got: cond, want: true });
    const noThrow = (name, fn) => {
      try { fn(); c.cases.push({ name, pass: true, got: 'ok', want: 'ok' }); }
      catch (e) { c.cases.push({ name, pass: false, got: String((e && e.message) || e), want: 'ok' }); }
    };
    return { eq, ok, noThrow };
  }
  return { cat, cats };
}

// Mirror EXACT de la formule backend AdminController (coût par client).
// Verrou : les 3 postes (SMS/IA/Mailjet) doivent TOUS entrer dans le coût.
function clientCost({ smsSeg = 0, aiUsed = 0, mailjet = 0 }, units) {
  return +(smsSeg * units.sms + aiUsed * units.ai + mailjet * units.mailjet).toFixed(2);
}

export function runAppeeSuite() {
  const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const S = makeSuite();

  // ── 🎂 DATES & ÂGE (verrou du bug « 1951 ans » / année 0074) ──────────────
  {
    const d = S.cat('dates', 'dates & âge');
    d.eq('année 2 chiffres 0074 → 1974', normalizeBirthDate('0074-10-01'), '1974-10-01');
    d.eq('année 4 chiffres inchangée', normalizeBirthDate('1974-10-01'), '1974-10-01');
    d.eq('pivot récent 0005 → 2005', normalizeBirthDate('0005-06-15'), '2005-06-15');
    d.eq('ISO datetime tronqué', normalizeBirthDate('1980-08-19T00:00:00.000000Z'), '1980-08-19');
    d.eq('sentinelle année ≤1904 → null', normalizeBirthDate('1900-01-01'), null);
    d.eq('date future → null', normalizeBirthDate('2050-01-01'), null);
    d.eq('valeur illisible → null', normalizeBirthDate('nawak'), null);
    d.ok('âge 0074 = âge 1974 (liste≡fiche)', contactAge('0074-10-01') === contactAge('1974-10-01'));
    d.ok('âge jamais absurde (≤120)', (contactAge('0074-10-01') ?? 0) <= 120);
    d.eq('sentinelle jour/mois seul → pas d’âge (aperçu)', contactAge('1900-06-15'), null);
    d.eq('localDateStr local sans décalage UTC', localDateStr(new Date(2026, 0, 5, 2, 0, 0)), '2026-01-05');
  }

  // ── 📱 SMS (facturation au segment, miroir backend) ───────────────────────
  {
    const s = S.cat('sms', 'sms segments');
    s.eq('texte simple = 1 segment', smsInfo('Bonjour Marie').segments, 1);
    s.ok('160 GSM-7 = 1 segment', smsInfo('A'.repeat(160)).segments === 1);
    s.ok('161 GSM-7 = 2 segments', smsInfo('A'.repeat(161)).segments === 2);
    s.ok('accents FR courants restent GSM-7', smsInfo('éàèùìò').unicode === false);
    s.ok('un emoji bascule en Unicode', smsInfo('Merci 🎉').unicode === true);
    s.ok('Unicode 70 = 1 segment', smsInfo('ê'.repeat(70)).segments === 1);
    s.ok('Unicode 71 = 2 segments', smsInfo('ê'.repeat(71)).segments === 2);
    s.eq('vide = 1 segment mini', smsSegments(''), 1);
    s.eq('longueur opt-out Stop', SMS_OPTOUT_LEN, 56);
    // Mobile suisse : ne PAS oublier 074/075 (bug de ciblage SMS)
    s.ok('079 = mobile', isSwissMobile('079 175 68 65'));
    s.ok('075 = mobile (souvent oublié)', isSwissMobile('075 123 45 67'));
    s.ok('074 = mobile (souvent oublié)', isSwissMobile('+41 74 123 45 67'));
    s.ok('021 (fixe) ≠ mobile', !isSwissMobile('021 123 45 67'));
  }

  // ── 👥 DOUBLONS & TÉLÉPHONE (verrou du bug « 473 doublons ») ───────────────
  {
    const dup = S.cat('doublons', 'doublons & tél.');
    dup.eq('+4179… = 79… national', normalizePhone('+41791756865'), '791756865');
    dup.eq('0041… = 79… national', normalizePhone('0041791756865'), '791756865');
    dup.eq('079… = 79… national', normalizePhone('0791756865'), '791756865');
    dup.ok('3 formats du même n° concordent',
      normalizePhone('+41791756865') === normalizePhone('0791756865') &&
      normalizePhone('0791756865') === normalizePhone('0041791756865'));
    dup.eq('n° trop court → vide', normalizePhone('12345'), '');
    const base = [
      { id: 1, first_name: 'Marie', last_name: 'Brunner', phone: '+41791756865' },
      { id: 2, first_name: 'Marie', last_name: 'Brunner', phone: '079 175 68 65' },
      { id: 3, first_name: 'Léa', last_name: 'Roux', phone: '+41780000000' },
    ];
    dup.eq('même n° 2 formats = 1 doublon', findDuplicates(base).length, 1);
    dup.eq('compte de contacts en double', duplicateContactCount(base), 2);
  }

  // ── 📵 JOIGNABILITÉ ────────────────────────────────────────────────────────
  {
    const j = S.cat('joignabilite', 'joignabilité');
    const c = [
      { id: 1, email: null, phone: null },
      { id: 2, email: 'bad', phone: '123' },
      { id: 3, email: 'ok@test.ch', phone: '+41791756865' },
    ];
    j.eq('injoignables (ni mail ni tél)', unreachable(c).length, 1);
    j.eq('e-mails au format invalide', invalidEmails(c).length, 1);
    j.eq('téléphones invalides', invalidPhones(c).length, 1);
  }

  // ── 🛡️ CONSENTEMENT nLPD (null ≠ refus) ────────────────────────────────────
  {
    const l = S.cat('consentement', 'consentement nLPD');
    const c = [
      { id: 1, gdpr_consent: null },   // à demander — PAS exclu
      { id: 2, gdpr_consent: false },  // refus explicite — exclu
      { id: 3, gdpr_consent: true },   // accordé
    ];
    l.eq('seul le refus explicite est listé', noConsentList(c).length, 1);
    l.ok('null n’est jamais compté comme refus', !noConsentList([{ id: 9, gdpr_consent: null }]).length);
  }

  // ── ⭐ VIP & RÉTENTION ──────────────────────────────────────────────────────
  {
    const v = S.cat('vip', 'vip & rétention');
    v.ok('catégorie VIP', isVip({ category: 'VIP' }) === true);
    v.ok('engagement ≥75 = VIP', isVip({ engagement_score: 80 }) === true);
    v.ok('engagement faible ≠ VIP', isVip({ engagement_score: 10 }) === false);
    const old = new Date(); old.setFullYear(old.getFullYear() - 2);
    const recent = new Date();
    const c = [
      { id: 1, category: 'VIP', last_interaction: old.toISOString() },
      { id: 2, category: 'VIP', last_interaction: recent.toISOString() },
    ];
    v.eq('VIP dormant (>12 mois) détecté', vipDormant(c).length, 1);
  }

  // ── 🧠 INSIGHTS (priorisation légal > injoignable) ─────────────────────────
  {
    const i = S.cat('insights', 'insights priorisés');
    const contacts = [
      { id: 1, gdpr_consent: false, email: null, phone: null },
      { id: 2, gdpr_consent: false, email: 'x@y.ch' },
    ];
    const out = computeInsights({ contacts, tenant: {} });
    i.ok('au moins un insight remonte', out.length > 0);
    i.ok('trié par sévérité croissante', out.every((o, k) => k === 0 || out[k - 1].severity <= o.severity));
    i.eq('base vide = 0 insight', computeInsights({}).length, 0);
    i.eq('anniversaire injoignable ce mois',
      birthdaySoonUnreachable([{ id: 1, birth_date: localDateStr(), email: null, phone: null }]).length, 1);
    const q = quotaPressure({ contacts_used: 90, contacts_limit: 100, emails_used: 0, emails_limit: 100 });
    i.ok('pression quota ≥85% signalée', q.some(x => x.kind === 'contacts'));
  }

  // ── 🧩 PROFIL ENTREPRISE (source unique) ───────────────────────────────────
  {
    const p = S.cat('profil', 'profil entreprise');
    p.eq('profil vide = tous les champs manquants', missingEssentials({}).length, PROFILE_ESSENTIALS.length);
    const full = {};
    PROFILE_ESSENTIALS.forEach(f => { full[f.key] = 'x'; });
    p.eq('profil complet = 0 manquant', missingEssentials(full).length, 0);
    p.eq('lien direct vers le bon champ', essentialLink('sector'), '/settings?tab=entreprise&focus=sector');
  }

  // ── 💰 MARGE PAR CLIENT (miroir formule AdminController) ────────────────────
  {
    const m = S.cat('marge', 'coûts & marge');
    const units = { sms: 0.05, ai: 0.001, mailjet: 0.0008 };
    m.eq('coût = SMS + IA + Mailjet',
      clientCost({ smsSeg: 100, aiUsed: 200, mailjet: 500 }, units),
      +(100 * 0.05 + 200 * 0.001 + 500 * 0.0008).toFixed(2));
    m.ok('client sans usage = coût 0', clientCost({}, units) === 0);
    m.ok('marge = revenu − coût', (299 - clientCost({ smsSeg: 10 }, units)) < 299);
  }

  // ── 🛟 ROBUSTESSE (aucune entrée ne fait planter) ──────────────────────────
  {
    const r = S.cat('robustesse', 'robustesse');
    r.eq('contactAge(null)', contactAge(null), null);
    r.eq('normalizePhone(null)', normalizePhone(null), '');
    r.eq('smsInfo(null) = 1 segment', smsInfo(null).segments, 1);
    r.eq('findDuplicates([])', findDuplicates([]).length, 0);
    r.eq('missingEssentials(null) tous manquants', missingEssentials(null).length, PROFILE_ESSENTIALS.length);
    r.noThrow('computeInsights(undefined) ne plante pas', () => computeInsights(undefined));
    r.noThrow('unreachable(undefined) ne plante pas', () => unreachable(undefined));
  }

  // ── ✉️ SMS DEPUIS E-MAIL (verrou du bug « double prénom ») ──────────────────
  {
    const a = S.cat('sms_email', 'sms depuis e-mail');
    a.eq('corps vide → vide', smsFromEmailBody(''), '');
    const r1 = smsFromEmailBody('Bonjour {{prénom}}, merci de votre visite.');
    a.ok('un seul {{prénom}} (pas de double)', (r1.match(/\{\{\s*pr[ée]nom\s*\}\}/gi) || []).length === 1);
    a.ok('salutation « Bonjour » retirée', !/bonjour/i.test(r1));
    a.ok('HTML retiré', !smsFromEmailBody('<p>Offre <b>spéciale</b> ce week-end.</p>').includes('<'));
    a.ok('signé {{entreprise}}', smsFromEmailBody('Offre du moment.').endsWith('{{entreprise}}'));
    const long = smsFromEmailBody('Profitez de nos nouveautes qui arrivent bientot dans votre magasin prefere avec plein de surprises et de cadeaux pour vous et vos proches cette saison');
    a.ok('coupe propre (jamais au milieu d’un mot)', long.includes('…') || /[.!?]\s*—/.test(long));
  }

  // ── 📊 SCORE PROFIL IA (calcAiScore 0→100) ─────────────────────────────────
  {
    const b = S.cat('score_profil', 'score profil IA');
    b.eq('profil vide = 0', calcAiScore({}), 0);
    b.eq('identité seule = 20', calcAiScore({ name: 'x', sector: 'y', logo_url: 'z' }), 20);
    const full = {
      name: 1, sector: 1, logo_url: 1, description: 1, target_audience: 1, services: 1,
      unique_selling_point: 1, values: 1, website: 1, city: 1, business_phone: 1,
      geographic_zone: 1, facebook_url: 1, instagram_url: 1, linkedin_url: 1,
      google_mybusiness_url: 1, avg_client_value: 1, keywords: 1, competitors: 1,
    };
    b.eq('profil complet = 100', calcAiScore(full), 100);
    b.ok('jamais > 100', calcAiScore({ ...full, extra: 1 }) <= 100);
  }

  // ── 🗓️ NOM DE CAMPAGNE AUTO « (JJ.MM.AAAA) » ───────────────────────────────
  {
    const c = S.cat('nom_campagne', 'nom campagne auto');
    const d = new Date(2026, 7, 9); // 9 août 2026
    c.eq('date au format JJ.MM.AAAA', autoDateStr(d), '09.08.2026');
    c.eq('suggestion horodatée', autoCampaignName('Anniversaires', null, d), 'Anniversaires (09.08.2026)');
    c.eq('édition = nom inchangé', autoCampaignName('Promo été', 'edit-1', d), 'Promo été');
    c.eq('sans preset = vide', autoCampaignName('', null, d), '');
  }

  // ── 🏛️ CANTONS SUISSES (verrou du bug « départements FR ») ─────────────────
  {
    const c = S.cat('cantons', 'cantons suisses');
    c.eq('26 cantons officiels', SWISS_CANTONS.size, 26);
    c.ok('VD est suisse', isSwissCanton('VD'));
    c.ok('insensible à la casse (ge)', isSwissCanton('ge'));
    c.ok('Doubs (FR) écarté', !isSwissCanton('Doubs'));
    c.ok('département n° écarté', !isSwissCanton('75'));
    c.eq('filtre ne garde que le suisse',
      swissCantons([{ canton: 'GE' }, { canton: 'Doubs' }, { canton: 'VD' }, { canton: 'Bas-Rhin' }, { canton: null }]),
      ['GE', 'VD']);
  }

  // ── 🔎 RECHERCHE CONTACTS (verrou du bug « pollution e-mail ») ─────────────
  {
    const r = S.cat('recherche', 'recherche contacts');
    r.ok('requête vide = tout passe', contactMatchesQuery({ first_name: 'x' }, '') === true);
    r.ok('trouve par nom', contactMatchesQuery({ first_name: 'Marie', last_name: 'Brunner' }, 'brunner'));
    r.ok('trouve par ville', contactMatchesQuery({ city: 'Genève' }, 'genève'));
    r.ok('trouve par téléphone', contactMatchesQuery({ phone: '+41791756865' }, '7917'));
    r.ok('insensible à la casse/accents', contactMatchesQuery({ first_name: 'Léa' }, 'LÉA'));
    r.ok('mot du domaine SANS @ ne remonte pas',
      contactMatchesQuery({ first_name: 'Jean', last_name: 'Roux', email: 'jean@gmail.com' }, 'gmail') === false);
    r.ok('avec @ l’e-mail est cherché',
      contactMatchesQuery({ first_name: 'Jean', last_name: 'Roux', email: 'jean@gmail.com' }, '@gmail') === true);
  }

  // ── ✅ SCORE QUALITÉ CONTACT (source unique, /100) ─────────────────────────
  {
    const q = S.cat('qualite', 'score qualité');
    q.eq('fiche vide = 0', qualityScore({}), 0);
    q.eq('barème total = 100', QUALITY_FIELDS.reduce((s, f) => s + f.pts, 0), 100);
    const full = { first_name: 1, last_name: 1, email: 1, phone: 1, birth_date: 1, gender: 'madame', city: 1, relationship_memory: 1 };
    q.eq('fiche complète = 100', qualityScore(full), 100);
    q.eq('prénom+nom+email = 55', qualityScore({ first_name: 'x', last_name: 'y', email: 'e@t.ch' }), 55);
    q.eq('civilité non renseignée = 0 pt', qualityScore({ gender: 'non_renseigne' }), 0);
    q.eq('détail = 8 champs', qualityBreakdown({}).length, 8);
  }

  // ── 🎨 AVATAR (couleur déterministe & genrée) ──────────────────────────────
  {
    const av = S.cat('avatar', 'avatar déterministe');
    av.eq('prénom féminin = rose', getAvatarColor('Marie'), '#E91E8C');
    av.eq('prénom masculin/neutre = violet', getAvatarColor('Alexandre'), '#5A35FF');
    av.eq('vide = violet par défaut', getAvatarColor(''), '#5A35FF');
    av.ok('déterministe (même prénom = même couleur)', getAvatarColor('Julie') === getAvatarColor('julie'));
    av.eq('heuristique terminaison -ine = féminin', getAvatarColor('Capucine'), '#E91E8C');
    av.ok('style féminin = dégradé rose', getAvatarStyle('Marie').background.includes('E91E8C'));
  }

  // ── Agrégation ──────────────────────────────────────────────────────────────
  const cats = S.cats.map(c => {
    const passed = c.cases.filter(x => x.pass).length;
    return { ...c, passed, total: c.cases.length, ok: passed === c.cases.length };
  });
  const total = cats.reduce((n, c) => n + c.total, 0);
  const passed = cats.reduce((n, c) => n + c.passed, 0);
  const ms = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0);

  return { cats, total, passed, ms, allGreen: passed === total };
}
